/**
 * Safe area detection and mobile device behavior utilities
 */

export interface SafeAreaInfo {
  bottom: number;
  top: number;
  left: number;
  right: number;
  hasNavigationBar: boolean;
  isInFullscreen: boolean;
  browserType: string;
  deviceType: string;
}

/**
 * Detect if the device supports CSS safe area insets
 */
export const supportsSafeAreaInsets = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  // Create a test element to check if env() is supported
  const testEl = document.createElement('div');
  testEl.style.paddingBottom = 'env(safe-area-inset-bottom)';
  document.body.appendChild(testEl);
  
  const computed = window.getComputedStyle(testEl);
  const hasSupport = computed.paddingBottom !== '0px' && computed.paddingBottom !== '';
  
  document.body.removeChild(testEl);
  return hasSupport;
};

/**
 * Get the current safe area insets
 */
export const getSafeAreaInsets = (): SafeAreaInfo => {
  if (typeof window === 'undefined') {
    return {
      bottom: 0,
      top: 0,
      left: 0,
      right: 0,
      hasNavigationBar: false,
      isInFullscreen: false,
      browserType: 'unknown',
      deviceType: 'unknown'
    };
  }

  // Detect browser type
  const userAgent = navigator.userAgent.toLowerCase();
  const browserType = getBrowserType(userAgent);
  const deviceType = getDeviceType(userAgent);

  // Try to get safe area values using different methods
  const safeAreaBottom = getSafeAreaBottom();
  const viewportHeight = getViewportHeight();
  const screenHeight = window.screen.height;
  
  // Calculate if there's likely a navigation bar
  const hasNavigationBar = (screenHeight - viewportHeight) > 50;
  
  // Check if we're in fullscreen mode
  const isInFullscreen = isFullscreenMode();

  return {
    bottom: safeAreaBottom,
    top: getSafeAreaTop(),
    left: getSafeAreaLeft(),
    right: getSafeAreaRight(),
    hasNavigationBar,
    isInFullscreen,
    browserType,
    deviceType
  };
};

/**
 * Get safe area bottom value using multiple fallback methods
 */
const getSafeAreaBottom = (): number => {
  // Method 1: CSS env() function
  if (supportsSafeAreaInsets()) {
    const testEl = document.createElement('div');
    testEl.style.paddingBottom = 'env(safe-area-inset-bottom)';
    document.body.appendChild(testEl);
    
    const computed = window.getComputedStyle(testEl);
    const envValue = parseFloat(computed.paddingBottom);
    
    document.body.removeChild(testEl);
    
    if (!isNaN(envValue) && envValue > 0) {
      return envValue;
    }
  }

  // Method 2: Viewport height calculation
  const viewportHeight = window.visualViewport?.height || window.innerHeight;
  const documentHeight = document.documentElement.clientHeight;
  const heightDiff = documentHeight - viewportHeight;
  
  if (heightDiff > 20) { // Likely has navigation
    return Math.max(heightDiff, 34); // Minimum 34px for standard nav bars
  }

  // Method 3: Device-specific fallbacks
  const userAgent = navigator.userAgent.toLowerCase();
  
  if (userAgent.includes('iphone')) {
    // iPhone with home indicator
    return userAgent.includes('iphone') && window.screen.height >= 812 ? 34 : 20;
  }
  
  if (userAgent.includes('android')) {
    // Android navigation bar estimate
    return 48;
  }

  // Default safe fallback
  return 32;
};

/**
 * Get other safe area values
 */
const getSafeAreaTop = (): number => {
  if (supportsSafeAreaInsets()) {
    const testEl = document.createElement('div');
    testEl.style.paddingTop = 'env(safe-area-inset-top)';
    document.body.appendChild(testEl);
    
    const computed = window.getComputedStyle(testEl);
    const envValue = parseFloat(computed.paddingTop);
    
    document.body.removeChild(testEl);
    
    if (!isNaN(envValue)) {
      return envValue;
    }
  }
  return 0;
};

const getSafeAreaLeft = (): number => {
  if (supportsSafeAreaInsets()) {
    const testEl = document.createElement('div');
    testEl.style.paddingLeft = 'env(safe-area-inset-left)';
    document.body.appendChild(testEl);
    
    const computed = window.getComputedStyle(testEl);
    const envValue = parseFloat(computed.paddingLeft);
    
    document.body.removeChild(testEl);
    
    if (!isNaN(envValue)) {
      return envValue;
    }
  }
  return 0;
};

const getSafeAreaRight = (): number => {
  if (supportsSafeAreaInsets()) {
    const testEl = document.createElement('div');
    testEl.style.paddingRight = 'env(safe-area-inset-right)';
    document.body.appendChild(testEl);
    
    const computed = window.getComputedStyle(testEl);
    const envValue = parseFloat(computed.paddingRight);
    
    document.body.removeChild(testEl);
    
    if (!isNaN(envValue)) {
      return envValue;
    }
  }
  return 0;
};

/**
 * Get browser type from user agent
 */
const getBrowserType = (userAgent: string): string => {
  if (userAgent.includes('safari') && !userAgent.includes('chrome')) return 'safari';
  if (userAgent.includes('chrome')) return 'chrome';
  if (userAgent.includes('firefox')) return 'firefox';
  if (userAgent.includes('edge')) return 'edge';
  return 'unknown';
};

/**
 * Get device type from user agent
 */
const getDeviceType = (userAgent: string): string => {
  if (userAgent.includes('iphone')) return 'iphone';
  if (userAgent.includes('ipad')) return 'ipad';
  if (userAgent.includes('android')) return 'android';
  return 'unknown';
};

/**
 * Get current viewport height
 */
const getViewportHeight = (): number => {
  return window.visualViewport?.height || window.innerHeight;
};

/**
 * Check if browser is in fullscreen mode
 */
const isFullscreenMode = (): boolean => {
  return !!(
    document.fullscreenElement ||
    (document as any).webkitFullscreenElement ||
    (document as any).mozFullScreenElement ||
    (document as any).msFullscreenElement
  );
};

/**
 * Detect if virtual keyboard is likely visible
 */
const isVirtualKeyboardVisible = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  // Compare current viewport height to window height
  const currentViewportHeight = window.visualViewport?.height || window.innerHeight;
  const windowHeight = window.innerHeight;
  
  // If viewport height is significantly smaller, keyboard is likely visible
  const heightDifference = windowHeight - currentViewportHeight;
  return heightDifference > 150; // Threshold for keyboard detection
};

/**
 * Calculate recommended padding for chat input
 */
export const getRecommendedInputPadding = (): number => {
  const safeArea = getSafeAreaInsets();
  const isKeyboardVisible = isVirtualKeyboardVisible();
  
  // Base padding
  const basePadding = 16; // 1rem
  
  if (isKeyboardVisible) {
    // When keyboard is visible, browser handles most spacing
    // Just use minimal padding + safe area + small extra margin for keyboard
    return basePadding + Math.min(safeArea.bottom, 20) + 15; // Cap safe area + 15px extra for keyboard
  } else {
    // When keyboard is hidden, add extra margin for navigation bars
    const extraMargin = 12; // Extra space for better UX
    return basePadding + safeArea.bottom + extraMargin;
  }
};

/**
 * Listen for viewport changes (for dynamic navigation bars)
 */
export const onViewportChange = (callback: (safeArea: SafeAreaInfo) => void): (() => void) => {
  if (typeof window === 'undefined') return () => {};

  const handler = () => {
    callback(getSafeAreaInsets());
  };

  // Listen for various viewport change events
  window.addEventListener('resize', handler);
  window.addEventListener('orientationchange', handler);
  
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', handler);
  }

  // Return cleanup function
  return () => {
    window.removeEventListener('resize', handler);
    window.removeEventListener('orientationchange', handler);
    
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', handler);
    }
  };
};